import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Check, ArrowRight, Sparkles, X } from "lucide-react";

const DISMISS_KEY = "nutrilens.onboarding.dismissed";

export default function OnboardingChecklist() {
    const { user } = useAuth();
    const [healthDone, setHealthDone] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined") {
            setDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
        }
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/health-profile");
                if (!alive) return;
                const has =
                    !!(r.data?.description && r.data.description.trim()) ||
                    (r.data?.conditions && r.data.conditions.length > 0) ||
                    !!r.data?.has_blood_test;
                setHealthDone(has);
            } catch {
                setHealthDone(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    if (!user || dismissed || healthDone === null) return null;

    const profileDone = !!user.onboarded_profile;
    const allDone = profileDone && healthDone;
    if (allDone) return null;

    const total = 2;
    const done = (profileDone ? 1 : 0) + (healthDone ? 1 : 0);

    const dismiss = () => {
        try {
            window.localStorage.setItem(DISMISS_KEY, "1");
        } catch {
            /* ignore */
        }
        setDismissed(true);
    };

    return (
        <div
            className="nl-card mb-5 relative overflow-hidden"
            style={{ background: "#EAF0E9" }}
            data-testid="onboarding-checklist"
        >
            <button
                onClick={dismiss}
                aria-label="Dismiss onboarding"
                data-testid="onboarding-dismiss"
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/60 text-[#4A5A4E]"
            >
                <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-start gap-3">
                <Sparkles
                    className="w-5 h-5 mt-0.5 shrink-0"
                    style={{ color: "#3B824E" }}
                />
                <div className="min-w-0 flex-1">
                    <p className="font-eyebrow" style={{ color: "#3B824E" }}>
                        Get started · {done} of {total}
                    </p>
                    <h2 className="font-serif-editorial text-2xl mt-1 text-[#1A3626] leading-tight">
                        Two quick steps for personalized scores.
                    </h2>
                    <p className="text-sm text-[#4A5A4E] mt-1">
                        We'll use this to tailor every scan to <em>your</em> body.
                    </p>

                    <div className="mt-4 space-y-2">
                        <Step
                            done={profileDone}
                            num={1}
                            title="Confirm your profile"
                            sub="Make sure your display name looks right."
                            to="/settings?tab=profile&onboarding=1"
                            testid="onboarding-step-profile"
                        />
                        <Step
                            done={healthDone}
                            num={2}
                            title="Add your health context"
                            sub="Tick conditions or note BP, A1C, meds, allergies."
                            to="/settings?tab=health&onboarding=1"
                            testid="onboarding-step-health"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function Step({ done, num, title, sub, to, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="flex items-center gap-3 p-3 rounded-2xl bg-white/70 hover:bg-white transition-colors group"
        >
            <span
                className={
                    "w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-medium shrink-0 " +
                    (done
                        ? "bg-[#3B824E] text-white"
                        : "bg-[#1A3626] text-[#F9F9F6]")
                }
            >
                {done ? <Check className="w-3.5 h-3.5" /> : num}
            </span>
            <div className="min-w-0 flex-1">
                <p
                    className={
                        "text-sm font-medium leading-tight " +
                        (done ? "text-[#3B824E] line-through" : "text-[#1A3626]")
                    }
                >
                    {title}
                </p>
                <p className="text-[12px] text-[#4A5A4E] leading-snug mt-0.5 truncate">
                    {sub}
                </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#8BA092] group-hover:text-[#1A3626] group-hover:translate-x-0.5 transition-all" />
        </Link>
    );
}
